/**
 * A breadcrumb trail through the app's start-up, kept in sessionStorage.
 *
 * It exists for the failure that leaves nothing to look at: the page comes up
 * blank and stays blank, and the console holds nothing that says why. A
 * start-up can stall in several places that look identical from outside — the
 * local database blocking on another window, a migration grinding, a request
 * that never answers.
 *
 * Each mark is written SYNCHRONOUSLY, so the last one survives a hang that
 * blocks the thread for ever. The previous attempt is kept too: after a hang
 * the device is reloaded, and by then the interesting run is the one before.
 */

import { defineStoredJson } from './storage';

/** One step, with the milliseconds since this document started loading. */
export interface BootMark {
    phase: string;
    at: number;
}

function defineTrace(key: string) {
    return defineStoredJson<BootMark[]>({
        key,
        area: 'session',
        fallback: () => [],
        decode: (parsed) => (Array.isArray(parsed) ? parsed : undefined)
    });
}

const currentTrace = defineTrace('boot_trace');
const previousTrace = defineTrace('boot_trace_previous');

/** The current attempt's marks, oldest first. */
export function readBootTrace(): BootMark[] {
    return currentTrace.read();
}

/** The attempt before this one — the interesting one after a hang + reload. */
export function readPreviousBootTrace(): BootMark[] {
    return previousTrace.read();
}

/** `start 3 ms › db:opening 96 ms › db:created 210 ms` — as a person reads it. */
export function formatBootTrace(marks: readonly BootMark[]): string {
    return marks
        .map((mark) => `${mark.phase} ${Math.round(mark.at)} ms`)
        .join(' › ');
}

/**
 * Note that start-up reached `phase`. Cheap and never throws: a blocked or full
 * sessionStorage must not be the thing that breaks the start it is watching.
 */
export function markBoot(phase: string): void {
    // No trace, no harm — it is a diagnostic, not a feature.
    currentTrace.write([
        ...currentTrace.read(),
        { phase, at: performance.now() }
    ]);
}

/**
 * Note when the page goes to the background and back until start-up is done.
 * A phone suspends a hidden page, so a start that "took twelve minutes" was
 * mostly spent in somebody's pocket — the trace has to say so.
 */
function traceVisibility(): void {
    if (typeof document === 'undefined') {
        return;
    }
    if (document.visibilityState === 'hidden') {
        markBoot('hidden');
    }
    const onChange = () => {
        if (readBootTrace().some((mark) => mark.phase === 'app')) {
            document.removeEventListener('visibilitychange', onChange);
            return;
        }
        markBoot(document.visibilityState === 'hidden' ? 'hidden' : 'visible');
    };
    document.addEventListener('visibilitychange', onChange);
}

/**
 * Begin a new trace, moving the last one aside. Call once, as early as the app
 * runs at all — a trace that never reaches its first mark means the failure is
 * in module loading, not in the app's own start-up.
 */
export function beginBootTrace(): void {
    const last = currentTrace.read();
    if (last.length > 0) {
        previousTrace.write(last);
    }
    currentTrace.clear();
    markBoot('start');
    traceVisibility();

    // If the run before this one never got to the end, say so once — this is the
    // line somebody will find in the console after a blank-page report.
    const previous = readPreviousBootTrace();
    if (previous.length && !previous.some((mark) => mark.phase === 'app')) {
        console.warn(
            `[boot] der vorherige Start kam nicht durch: ${formatBootTrace(previous)}`
        );
    }
}
